import { useId, useRef } from 'react'
import { Icon } from '../primitives/Icon'
import {
  UNSUPPORTED_UPLOAD_MESSAGE,
  UPLOAD_ACCEPT_ATTR,
  isAcceptedUploadFile,
} from '../../lib/upload-accept'

export type StagedAiVerdict = 'match' | 'partial' | 'mismatch'

export interface StagedFile {
  /** Stable id for React keying — generated when the file is staged. */
  id: string
  file: File
  status: 'staged' | 'uploading' | 'uploaded' | 'error'
  /** Populated after a successful upload — used for AI review etc. */
  remoteId?: string
  /** Populated when status is 'error'. */
  error?: string
}

export interface StagedDocumentPickerProps {
  files: ReadonlyArray<StagedFile>
  onChange: (next: StagedFile[]) => void
  /** Called once with the freshly-picked files (id + File object) the
   *  moment they're added. */
  onFilesPicked?: (newFiles: ReadonlyArray<{ id: string; file: File }>) => void
  /** Disable file picking after the leave has been submitted; the page
   *  will swap into the live DocumentChips upload widget once the leave
   *  row exists. */
  disabled?: boolean
  /** When set, identifies the type of doc the user should be uploading
   *  ("Medical certificate", "Marriage certificate", …) — used in the
   *  picker hint. */
  evidenceLabel?: string
}

/**
 * Pre-submit document staging widget.
 *
 * Stores selected files in client-side state (no upload yet) so the
 * user can see and curate the doc list before submitting the leave.
 * The page is responsible for flushing the staged files via
 * `uploadDocument(leaveId, file)` after the leave row is created.
 *
 * Render contract:
 *   - "Add file" button + hidden file input.
 *   - One row per staged file with name, size, status badge, and a
 *     remove button (disabled while uploading / after upload).
 */
export function StagedDocumentPicker({
  files,
  onChange,
  onFilesPicked,
  disabled = false,
  evidenceLabel,
}: StagedDocumentPickerProps): JSX.Element {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)

  function handlePick(event: React.ChangeEvent<HTMLInputElement>): void {
    const picked = Array.from(event.target.files ?? [])
    if (picked.length === 0) return
    if (inputRef.current) inputRef.current.value = ''
    // `accept` on the input only hints the OS dialog — a user can still pick
    // "All files" and choose an unsupported type (e.g. an iPhone .HEIC). Stage
    // those straight into the 'error' state so they show a red 'Failed' badge +
    // reason and are never flushed (the flush only uploads 'staged' files),
    // rather than letting them fail server-side on a round-trip.
    const newlyStaged: StagedFile[] = picked.map((file) => {
      const id = `staged-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      return isAcceptedUploadFile(file)
        ? { id, file, status: 'staged' as const }
        : { id, file, status: 'error' as const, error: UNSUPPORTED_UPLOAD_MESSAGE }
    })
    onChange([...files, ...newlyStaged])
    // Only announce the files that will actually upload.
    const accepted = newlyStaged.filter((f) => f.status === 'staged')
    if (onFilesPicked && accepted.length > 0) {
      onFilesPicked(accepted.map((f) => ({ id: f.id, file: f.file })))
    }
  }

  function handleRemove(id: string): void {
    onChange(files.filter((f) => f.id !== id))
  }

  return (
    <div
      data-testid="staged-document-picker"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {/* Styled trigger — the raw <input type=file> ("Choose Files / no
          file chosen") is visually hidden and driven by this clickable
          dashed dropzone so the control matches the rest of the app. */}
      <label
        htmlFor={inputId}
        data-testid="staged-document-picker-trigger"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '16px',
          border: '1.5px dashed var(--french-gray-2)',
          borderRadius: 10,
          background: 'var(--surface)',
          color: 'var(--text-2)',
          fontSize: 13,
          fontWeight: 500,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Icon name="attach_file" size={18} aria-hidden="true" />
        {evidenceLabel ? `Choose ${evidenceLabel}` : 'Attach supporting document'}
      </label>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple
        accept={UPLOAD_ACCEPT_ATTR}
        onChange={handlePick}
        disabled={disabled}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          border: 0,
        }}
      />
      {files.length > 0 ? (
        <ul
          role="list"
          data-testid="staged-files"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            margin: 0,
            padding: 0,
            listStyle: 'none',
          }}
        >
          {files.map((f) => (
            <li
              key={f.id}
              data-testid="staged-file-row"
              data-status={f.status}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '1px solid var(--french-gray-2)',
                borderRadius: 8,
                background: 'var(--surface)',
                fontSize: 12,
              }}
            >
              <Icon name="attach_file" size={16} aria-hidden="true" />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <span
                  style={{
                    color: 'var(--text)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {f.file.name}
                </span>
                <span style={{ color: 'var(--text-3)', fontSize: 11 }}>
                  {humanSize(f.file.size)}
                  {f.error ? ` · ${f.error}` : ''}
                </span>
              </span>
              <StatusBadge status={f.status} />
              {f.status === 'staged' || f.status === 'error' ? (
                <button
                  type="button"
                  onClick={() => handleRemove(f.id)}
                  aria-label={`Remove ${f.file.name}`}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-3)',
                    cursor: 'pointer',
                    fontSize: 14,
                    padding: 4,
                  }}
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

interface StatusBadgeProps {
  status: StagedFile['status']
}

function StatusBadge({ status }: StatusBadgeProps): JSX.Element {
  const style =
    status === 'uploaded'
      ? {
          background: 'var(--success)',
          color: 'var(--surface)',
          label: 'Uploaded',
        }
      : status === 'uploading'
        ? {
            background: 'var(--warning)',
            color: 'var(--surface)',
            label: 'Uploading…',
          }
        : status === 'error'
          ? {
              background: 'var(--danger)',
              color: 'var(--surface)',
              label: 'Failed',
            }
          : {
              background: 'var(--surface-2)',
              color: 'var(--text-2)',
              label: 'Ready to upload',
            }
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        background: style.background,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {style.label}
    </span>
  )
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
